import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { LogoDeca } from './LogoDeca';
import {
  DicaDoMenu,
  gravarRecolhido,
  iniciaisDe,
  lerRecolhido,
  useTelaLarga,
} from './MenuRecolhivel';
import { useCarregarSiscred } from '../servicos/siscred';
import { SinoDeNotificacoes } from './SinoDeNotificacoes';
import { LimiteDeErro } from '../componentes/LimiteDeErro';
import { Botao } from '../componentes/ui';
import { useEu, useNomeDaEmpresa } from '../hooks/useEu';
import { useParticipacao } from '../hooks/useParticipacao';
import { assessoraEmAlgum, type PapelNoServico, type Participacao } from '../servicos/participacao';
import { useEmpresaCorrente, useIdentidade, useSair, useVinculoCorrente } from '../hooks/useSessao';
import { TrocarEmpresa } from './TrocarEmpresa';

/**
 * Casca das telas autenticadas.
 *
 * ## Por que uma casca, e não um cabeçalho por tela
 *
 * Antes desta camada só existia o `Painel`, e ele carregava seu próprio
 * cabeçalho, seletor de empresa e botão de sair. Com sete telas, repetir isso
 * seria sete lugares para desatualizar — e a barra lateral precisa saber qual
 * item está ativo, o que só o roteador sabe.
 *
 * A navegação fica em `<Outlet/>`: cada tela cuida do seu conteúdo e nada mais.
 *
 * ## O que cada um vê
 *
 * O sistema é um só. O admin — a **operadora**, quem opera a intermediação —
 * vê o menu inteiro (`MENU`); quem não é admin vê **o mesmo menu, filtrado**
 * pelo que é da empresa escolhida (`menuDaParte`): os mesmos itens, nos mesmos
 * lugares, cada tela mostrando só o que é dela. Trocar de empresa troca o
 * menu.
 *
 * Esconder o item de menu **não** é o controle de acesso: quem controla é a
 * matriz no servidor, e ela responde 403 para a mesma rota chamada na mão. O
 * menu filtrado existe para não oferecer o que vai ser recusado.
 */

export interface ItemDeMenu {
  readonly para: string;
  readonly rotulo: string;
  readonly icone: JSX.Element;
  /**
   * Um nível abaixo do item, recuado — o CRM e as telas dele. O item-pai abre e
   * recolhe os subitens (como as divisões, com chevron) e fica aceso enquanto
   * qualquer um deles estiver aberto.
   */
  readonly subitens?: readonly ItemDeMenu[];
  /** Com subitens: a chave de persistência do aberto/fechado, como a das divisões. */
  readonly colapsavel?: string;
}

export interface GrupoDeMenu {
  readonly titulo: string | null;
  readonly itens: readonly ItemDeMenu[];
  /**
   * Chave de persistência do aberto/fechado. Só as divisões de serviço a têm:
   * "Painel" e "Gestão" ficam sempre à vista, porque são o caminho de todo
   * dia — recolher a gestão seria esconder Empresas de quem entra para abrir
   * uma empresa.
   */
  readonly colapsavel?: string;
}

/**
 * O menu do admin, de cima para baixo: o painel, a **gestão** (quem participa
 * da operação, e o CRM que acompanha cada empresa) e as **divisões de serviço**
 * (o que se faz com elas).
 *
 * ## Por que a gestão vem antes das divisões
 *
 * Um cliente é um só, e pode estar no Recorrente e no Passivo ao mesmo tempo.
 * Se Clientes morasse dentro de uma divisão, o mesmo cliente teria dois
 * endereços — e quem procura "a empresa X" não sabe, nem deveria precisar
 * saber, em qual divisão ela está antes de abri-la. As abas de solução ficam
 * dentro do perfil do cliente, e só aparecem quando há dado daquela solução.
 *
 * ## As divisões recolhem
 *
 * Cada divisão é uma pasta com chevron, aberta por padrão, e o navegador lembra
 * o que foi recolhido (`localStorage`). É o comportamento do Deca 1, que tinha
 * quatro níveis de pasta; aqui é um só — o item que navega fica logo abaixo do
 * título, sem subpasta.
 */
const MENU: readonly GrupoDeMenu[] = [
  {
    titulo: null,
    itens: [
      { para: '/painel', rotulo: 'Painel', icone: <IconePainel /> },
      // A assessora de IA (D-013). Só do admin: `menuDaParte` lista os caminhos
      // que cada um vê, e este não está lá.
      { para: '/beca', rotulo: 'BECA IA', icone: <IconeBeca /> },
    ],
  },
  {
    titulo: 'Gestão',
    // Clientes, fornecedores e assessores são abas de Empresas (`?perfil=`).
    // A antiga carteira de clientes virou Negociações do Recorrente, que é para
    // onde o sino aponta; fornecedores e assessores só saíram do menu.
    itens: [
      { para: '/empresas', rotulo: 'Empresas', icone: <IconeEmpresas /> },
      { para: '/pessoas', rotulo: 'Pessoas', icone: <IconePessoas /> },
      {
        para: '/crm',
        rotulo: 'CRM',
        icone: <IconeCrm />,
        colapsavel: 'crm',
        subitens: [
          { para: '/crm/pipeline', rotulo: 'Pipeline', icone: <IconePipeline /> },
          { para: '/crm/atividades', rotulo: 'Atividades', icone: <IconeAtividades /> },
          { para: '/crm/relatorios', rotulo: 'Relatórios', icone: <IconeRelatorios /> },
          // Solução em teste (30/09/2026) — sair é tirar esta linha, a rota e a pasta.
          { para: '/crm/prospeccao', rotulo: 'Prospecção', icone: <IconeProspeccao /> },
        ],
      },
    ],
  },
  {
    titulo: 'Créditos ICMS Recorrente',
    colapsavel: 'recorrente',
    itens: [
      { para: '/simulacoes', rotulo: 'Simulações', icone: <IconeSimulacoes /> },
      {
        para: '/icms-recorrente/negociacoes',
        rotulo: 'Negociações',
        icone: <IconeContratos />,
      },
    ],
  },
  {
    titulo: 'Créditos ICMS Passivos',
    colapsavel: 'passivo',
    itens: [
      { para: '/passivo', rotulo: 'Simulações', icone: <IconePassivo /> },
      {
        para: '/passivo/contratos',
        rotulo: 'Contratos',
        icone: <IconeContratos />,
      },
    ],
  },
  {
    titulo: 'Créditos ICMS Pontual',
    colapsavel: 'pontual',
    itens: [
      {
        para: '/icms-pontual',
        rotulo: 'Negociações',
        icone: <IconeContratos />,
      },
    ],
  },
  {
    /*
      O que não é carteira nem divisão: os pré-cadastros que chegam pelo
      formulário público e o catálogo de serviços da operadora. Não têm lugar
      nas divisões (um pré-cadastro ainda não é de nenhuma) e não são carteira.
      E a Central de Configurações, que atravessa todas as divisões.
    */
    titulo: 'Cadastros',
    itens: [
      { para: '/aprovacao', rotulo: 'Em aprovação', icone: <IconeAprovacao /> },
      { para: '/servicos', rotulo: 'Serviços', icone: <IconeServicosMenu /> },
      // As configurações de cada divisão e as automações, num lugar só.
      { para: '/configuracoes', rotulo: 'Configurações', icone: <IconeConfiguracoes /> },
    ],
  },
];

/**
 * O menu de quem não é admin: o do admin, filtrado pela **empresa escolhida**.
 *
 * - **Painel** e **Empresas** (Gestão), sempre: o painel da empresa e o
 *   cadastro dela — dados, conta bancária, colaboradores;
 * - cada **divisão** aparece quando a empresa tem papel nela (`GET
 *   /participacao`, pela operação), com as telas de acompanhar: Negociações do
 *   Recorrente, Contratos do Passivo, Negociações do Pontual. Cada tela lista
 *   só o que é da empresa — os clientes, para quem assessora;
 * - quem **assessora** vê as três divisões: simula para os clientes em
 *   qualquer uma, inclusive na que ainda não tem contrato — no Passivo, com a
 *   página de Simulações, como o admin.
 *
 * As telas de operação (simulações da captação, configurações, CRM, pessoas,
 * cadastros) continuam só do admin. Enquanto a participação não chega, só
 * Painel e Empresas: o menu cresce, em vez de mostrar uma divisão que some.
 */
export function menuDaParte(participacao: Participacao | undefined): GrupoDeMenu[] {
  const grupo = (titulo: string | null): GrupoDeMenu => {
    const achado = MENU.find((g) => g.titulo === titulo);
    if (achado === undefined) throw new Error(`Grupo de menu inexistente: ${String(titulo)}`);
    return achado;
  };
  /** O grupo do admin com só os itens listados, na ordem dele. */
  const soCom = (titulo: string | null, caminhos: readonly string[]): GrupoDeMenu => {
    const g = grupo(titulo);
    return { ...g, itens: g.itens.filter((i) => caminhos.includes(i.para)) };
  };

  const base = [soCom(null, ['/painel']), soCom('Gestão', ['/empresas'])];
  if (participacao === undefined) return base;

  const assessora = assessoraEmAlgum(participacao);
  const divisao = (
    titulo: string,
    papeis: readonly PapelNoServico[],
    caminhos: readonly string[],
  ): GrupoDeMenu[] => (papeis.length > 0 || assessora ? [soCom(titulo, caminhos)] : []);

  return [
    ...base,
    ...divisao('Créditos ICMS Recorrente', participacao.recorrente, [
      '/icms-recorrente/negociacoes',
    ]),
    // O assessor também simula no Passivo — às vezes só quer o número, sem
    // pedir fechamento. Quem é só cliente ou fornecedor acompanha os contratos.
    ...divisao(
      'Créditos ICMS Passivos',
      participacao.passivo,
      assessora ? ['/passivo', '/passivo/contratos'] : ['/passivo/contratos'],
    ),
    ...divisao('Créditos ICMS Pontual', participacao.pontual, ['/icms-pontual']),
  ];
}

/** Prefixo das chaves de aberto/fechado no `localStorage`, uma por divisão. */
const CHAVE_MENU = 'deca.menu.aberto.';

function lerAberto(chave: string): boolean {
  try {
    // Só "fechado" é gravado; ausência é "aberto", que é o padrão.
    return window.localStorage.getItem(CHAVE_MENU + chave) !== '0';
  } catch {
    return true;
  }
}

function gravarAberto(chave: string, aberto: boolean): void {
  try {
    if (aberto) window.localStorage.removeItem(CHAVE_MENU + chave);
    else window.localStorage.setItem(CHAVE_MENU + chave, '0');
  } catch {
    // Sem armazenamento a escolha vale só até recarregar — melhor que travar o menu.
  }
}

export function CascaDoApp(): JSX.Element {
  const identidade = useIdentidade();
  // O ano vigente do Recorrente depende dos SISCRED encerrados (27/10/2026).
  useCarregarSiscred();
  const [menuAberto, setMenuAberto] = useState(false);
  const local = useLocation();

  // Recolhido = só os ícones. Vale no computador; no celular a lateral é
  // gaveta e abre sempre inteira.
  const telaLarga = useTelaLarga();
  const [recolherPedido, setRecolherPedido] = useState(lerRecolhido);
  const recolhido = recolherPedido && telaLarga;
  const alternarRecolhido = (): void => {
    setRecolherPedido((atual) => {
      gravarRecolhido(!atual);
      return !atual;
    });
  };

  // Lido uma vez, na montagem: o menu não precisa reagir a outra aba do
  // navegador mudando a preferência.
  const [abertos, setAbertos] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      MENU.flatMap((g) =>
        g.colapsavel === undefined ? [] : [[g.colapsavel, lerAberto(g.colapsavel)]],
      ),
    ),
  );

  const alternar = (chave: string): void => {
    setAbertos((atual) => {
      const proximo = !(atual[chave] ?? true);
      gravarAberto(chave, proximo);
      return { ...atual, [chave]: proximo };
    });
  };

  const participacao = useParticipacao();
  // O admin vê o menu inteiro; quem não é admin, o da empresa escolhida.
  const grupos = identidade?.isAdmin === true ? MENU : menuDaParte(participacao.data);

  return (
    <div className="flex min-h-screen bg-superficie-base text-conteudo-primario">
      {/*
        A lateral vira gaveta no celular. `md:translate-x-0` a devolve ao fluxo
        em tela grande sem precisar de um segundo componente.
      */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex shrink-0 flex-col bg-superficie-lateral text-conteudo-naLateral transition-[transform,width] duration-200 md:sticky md:top-0 md:h-screen md:translate-x-0 ${
          recolhido ? 'w-16' : 'w-64'
        } ${menuAberto ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div
          className={`flex h-16 items-center ${
            recolhido ? 'justify-center' : 'justify-between gap-2 pl-6 pr-3'
          }`}
        >
          {/* A lateral é escura nos dois temas, então a marca é sempre a branca. */}
          {!recolhido && (
            <NavLink to="/painel" aria-label="Deca — ir para o painel" className="inline-flex">
              <LogoDeca fundo="escuro" className="h-7" />
            </NavLink>
          )}
          {/* Recolher é coisa de tela grande: no celular a lateral é gaveta. */}
          <DicaDoMenu texto={recolhido ? 'Expandir menu' : 'Recolher menu'}>
            <button
              type="button"
              onClick={alternarRecolhido}
              aria-label={recolhido ? 'Expandir menu' : 'Recolher menu'}
              aria-expanded={!recolhido}
              className="hidden h-8 w-8 items-center justify-center rounded-card text-conteudo-naLateral/60 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano md:inline-flex"
            >
              <IconeRecolher recolhido={recolhido} />
            </button>
          </DicaDoMenu>
        </div>

        <nav
          aria-label="Seções do sistema"
          className={`flex flex-1 flex-col overflow-y-auto overflow-x-hidden py-4 ${
            recolhido ? 'gap-2 px-2' : 'gap-5 px-3'
          }`}
        >
          {grupos.map((grupo, i) => {
            const chave = grupo.colapsavel;
            // Recolhido, os títulos somem — e com eles o recolher de cada
            // divisão: todos os ícones ficam à vista.
            const aberto = recolhido || chave === undefined ? true : (abertos[chave] ?? true);
            // Uma divisão recolhida com a tela atual dentro dela ganha o
            // destaque no título: o usuário sabe onde está mesmo sem abrir.
            const contemAtual =
              !aberto && grupo.itens.some((item) => local.pathname.startsWith(item.para));
            const idDaLista = `menu-${chave ?? i}`;

            return (
              <div key={grupo.titulo ?? `grupo-${i}`} className="flex flex-col gap-1">
                {/* Recolhido, cada grupo é separado por uma linha, no lugar do título. */}
                {recolhido && i > 0 && <hr className="mx-2 mb-1 border-white/10" />}
                {grupo.titulo !== null &&
                  !recolhido &&
                  (chave === undefined ? (
                    <p className="px-3 pb-1 text-[0.7rem] font-semibold uppercase tracking-wider text-conteudo-naLateral/40">
                      {grupo.titulo}
                    </p>
                  ) : (
                    <button
                      type="button"
                      aria-expanded={aberto}
                      aria-controls={idDaLista}
                      onClick={() => alternar(chave)}
                      className={`flex w-full items-center justify-between rounded-card px-3 py-1.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider transition-colors hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano ${
                        contemAtual ? 'text-white' : 'text-conteudo-naLateral/40 hover:text-white'
                      }`}
                    >
                      <span>{grupo.titulo}</span>
                      <IconeChevron aberto={aberto} />
                    </button>
                  ))}
                {aberto && (
                  <div id={idDaLista} className="flex flex-col gap-1">
                    {grupo.itens.map((item) =>
                      item.subitens !== undefined ? (
                        <ItemComSubitens
                          key={item.para}
                          item={item}
                          recolhido={recolhido}
                          aoNavegar={() => setMenuAberto(false)}
                        />
                      ) : (
                        <DicaDoMenu
                          key={item.para}
                          // As divisões repetem nomes ("Simulações", "Configurações"):
                          // recolhido, a dica diz de qual é.
                          texto={
                            chave === undefined || grupo.titulo === null
                              ? item.rotulo
                              : `${item.rotulo} · ${grupo.titulo}`
                          }
                          {...(recolhido ? {} : { desligada: true })}
                        >
                          <NavLink
                            to={item.para}
                            // `/passivo` casaria com `/passivo/contratos` sem isto —
                            // dois itens acesos ao mesmo tempo.
                            end={grupo.itens.some(
                              (outro) => outro !== item && outro.para.startsWith(`${item.para}/`),
                            )}
                            onClick={() => setMenuAberto(false)}
                            className={({ isActive }) =>
                              `flex items-center gap-3 rounded-card py-2 text-sm transition-colors ${
                                recolhido ? 'justify-center px-0' : 'px-3'
                              } ${
                                isActive
                                  ? 'bg-white/10 font-medium text-white'
                                  : 'text-conteudo-naLateral/70 hover:bg-white/5 hover:text-white'
                              }`
                            }
                          >
                            {item.icone}
                            {/* Recolhido, o nome continua para o leitor de tela. */}
                            <span className={recolhido ? 'sr-only' : undefined}>{item.rotulo}</span>
                          </NavLink>
                        </DicaDoMenu>
                      ),
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <IdentidadeDoRodape recolhido={recolhido} />
      </aside>

      {/* Véu que fecha a gaveta no celular. */}
      {menuAberto && (
        <button
          type="button"
          aria-label="Fechar menu"
          onClick={() => setMenuAberto(false)}
          className="fixed inset-0 z-30 bg-superficie-base/70 md:hidden"
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <BarraSuperior aoAbrirMenu={() => setMenuAberto(true)} />

        {/* `min-w-0` para tabela larga rolar dentro do conteúdo, não empurrar a página. */}
        <main className="min-w-0 flex-1 px-4 py-8 sm:px-8">
          {/*
            O limite de erro fica **dentro** da casca, com `key` na rota: um erro
            de tela não derruba a navegação, e trocar de tela limpa o estado de
            erro anterior — sem isso, uma tela quebrada mantém a mensagem depois
            de o usuário já ter ido para outra.
          */}
          <LimiteDeErro key={local.pathname} area="esta tela">
            <Outlet />
          </LimiteDeErro>
        </main>
      </div>
    </div>
  );
}

/**
 * O item com subitens — hoje, o CRM.
 *
 * O item-pai é um botão que abre e recolhe os subitens, com o chevron das
 * divisões e a mesma memória delas (`localStorage`): quem não usa o CRM no dia
 * deixa só uma linha. Recolhido com a tela atual dentro dele, o item fica
 * aceso — o usuário sabe onde está mesmo sem abrir.
 */
function ItemComSubitens(props: {
  readonly item: ItemDeMenu;
  readonly recolhido: boolean;
  readonly aoNavegar: () => void;
}): JSX.Element {
  const local = useLocation();
  const subitens = props.item.subitens ?? [];
  const dentro = local.pathname.startsWith(props.item.para);
  const chave = props.item.colapsavel ?? props.item.para;
  const [aberto, setAberto] = useState(() => lerAberto(chave));
  const idDaLista = `submenu-${chave.replace(/[^a-z0-9]/gi, '')}`;

  const alternar = (): void => {
    const proximo = !aberto;
    gravarAberto(chave, proximo);
    setAberto(proximo);
  };

  // Recolhido, o item-pai só abriria e fecharia: os subitens vão direto como
  // ícones, e a dica diz de quem são ("Pipeline · CRM").
  if (props.recolhido) {
    return (
      <div className="flex flex-col gap-1">
        {subitens.map((sub) => (
          <DicaDoMenu key={sub.para} texto={`${sub.rotulo} · ${props.item.rotulo}`}>
            <NavLink
              to={sub.para}
              onClick={props.aoNavegar}
              className={({ isActive }) =>
                `flex items-center justify-center rounded-card py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-white/10 font-medium text-white'
                    : 'text-conteudo-naLateral/70 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              {sub.icone}
              <span className="sr-only">
                {sub.rotulo} ({props.item.rotulo})
              </span>
            </NavLink>
          </DicaDoMenu>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        aria-expanded={aberto}
        aria-controls={idDaLista}
        onClick={alternar}
        className={`flex w-full items-center gap-3 rounded-card px-3 py-2 text-left text-sm transition-colors hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano ${
          dentro ? 'font-medium text-white' : 'text-conteudo-naLateral/70 hover:text-white'
        }`}
      >
        {props.item.icone}
        <span className="flex-1">{props.item.rotulo}</span>
        <IconeChevron aberto={aberto} />
      </button>
      {aberto && (
        <div id={idDaLista} className="ml-5 flex flex-col gap-1 border-l border-white/10 pl-3">
          {subitens.map((sub) => (
            <NavLink
              key={sub.para}
              to={sub.para}
              onClick={props.aoNavegar}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-card px-3 py-1.5 text-sm transition-colors ${
                  isActive
                    ? 'bg-white/10 font-medium text-white'
                    : 'text-conteudo-naLateral/70 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              {sub.icone}
              {sub.rotulo}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function BarraSuperior(props: { readonly aoAbrirMenu: () => void }): JSX.Element {
  const empresaId = useEmpresaCorrente();
  const nomeDaEmpresa = useNomeDaEmpresa(empresaId);
  const vinculo = useVinculoCorrente();
  const [trocando, setTrocando] = useState(false);
  const sair = useSair();
  const identidade = useIdentidade();

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-borda bg-superficie-base/80 px-4 backdrop-blur-vidro sm:px-8">
      <Botao
        variante="sutil"
        tamanho="pequeno"
        onClick={props.aoAbrirMenu}
        aria-label="Abrir menu"
        className="md:hidden"
      >
        <IconeMenu />
      </Botao>

      <div className="min-w-0 flex-1">
        {empresaId !== null && (
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-medium">{nomeDaEmpresa ?? 'Empresa'}</span>
            {vinculo !== null && (
              <span className="hidden shrink-0 text-xs text-conteudo-sutil sm:inline">
                {vinculo.papeis.includes('BUSINESS_OWNER') ? 'Gestor' : 'Colaborador'}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <SinoDeNotificacoes />
        {identidade?.isAdmin === true && (
          <span className="hidden rounded-full border border-acento-indigo/40 bg-acento-indigo/10 px-2.5 py-0.5 text-xs text-acento-indigo sm:inline">
            Operadora
          </span>
        )}
        {empresaId !== null && (
          <Botao variante="sutil" tamanho="pequeno" onClick={() => setTrocando(true)}>
            Trocar empresa
          </Botao>
        )}
        <Botao variante="secundario" tamanho="pequeno" onClick={() => sair.mutate()}>
          Sair
        </Botao>
      </div>

      <TrocarEmpresa aberto={trocando} aoFechar={() => setTrocando(false)} />
    </header>
  );
}

/**
 * Rodapé da lateral com quem está logado.
 *
 * O nome e o e-mail vêm de `GET /auth/eu`, não do token: `jwt-payload.ts` deixa
 * PII de fora de propósito — JWT é assinado, não criptografado, e qualquer um
 * que veja o token leria o e-mail. Enquanto a consulta não responde, mostra só o
 * papel, que o token traz.
 */
function IdentidadeDoRodape(props: { readonly recolhido: boolean }): JSX.Element {
  const identidade = useIdentidade();
  const eu = useEu();
  const nome = eu.data?.nome ?? eu.data?.emailAddress ?? 'Sessão ativa';
  const papel = identidade?.isAdmin === true ? 'Administrador' : 'Usuário';

  if (props.recolhido) {
    return (
      <div className="flex justify-center border-t border-white/10 py-4">
        <DicaDoMenu texto={`${nome} · ${papel}`}>
          <Link
            to="/perfil"
            aria-label={`${nome} — ${papel}. Abrir o perfil.`}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-white transition-colors hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
          >
            {iniciaisDe(nome)}
          </Link>
        </DicaDoMenu>
      </div>
    );
  }

  return (
    <div className="border-t border-white/10 px-6 py-4">
      <Link
        to="/perfil"
        className="block truncate text-sm text-white underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
      >
        {eu.data?.nome ?? eu.data?.emailAddress ?? 'Sessão ativa'}
      </Link>
      <p className="mt-0.5 text-xs text-conteudo-naLateral/60">
        {identidade?.isAdmin === true ? 'Administrador' : 'Usuário'}
      </p>
    </div>
  );
}

/* ─── Ícones ──────────────────────────────────────────────────────────────── */
/*
 * Traçado, 20px, `currentColor`. Ficam aqui e não em `componentes/ui/icones`
 * porque são de navegação: mudam com o menu, não com o design system.
 */

const traco = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function IconeConfiguracoes(): JSX.Element {
  return (
    <svg {...traco}>
      <path d="M4 7h10M18 7h2M4 12h2M10 12h10M4 17h10M18 17h2" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="8" cy="12" r="2" />
      <circle cx="16" cy="17" r="2" />
    </svg>
  );
}

function IconeChevron(props: { readonly aberto: boolean }): JSX.Element {
  return (
    <svg
      {...traco}
      width={14}
      height={14}
      className={`transition-transform ${props.aberto ? 'rotate-180' : ''}`}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function IconePessoas(): JSX.Element {
  return (
    <svg {...traco}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconePainel(): JSX.Element {
  return (
    <svg {...traco}>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  );
}

/** Um balão de conversa com uma faísca — a BECA. */
function IconeBeca(): JSX.Element {
  return (
    <svg {...traco}>
      <path d="M20 11.5a8 8 0 0 1-11.6 7.1L4 20l1.3-4.1A8 8 0 1 1 20 11.5z" />
      <path d="M12 7.5l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9z" />
    </svg>
  );
}

function IconeCrm(): JSX.Element {
  return (
    <svg {...traco}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" />
    </svg>
  );
}

function IconePipeline(): JSX.Element {
  return (
    <svg {...traco}>
      <rect x="3" y="4" width="5" height="16" rx="1" />
      <rect x="10" y="4" width="5" height="11" rx="1" />
      <rect x="17" y="4" width="4" height="7" rx="1" />
    </svg>
  );
}

function IconeAtividades(): JSX.Element {
  return (
    <svg {...traco}>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </svg>
  );
}

/** Lupa sobre uma lista: procurar quem tem crédito. */
function IconeProspeccao(): JSX.Element {
  return (
    <svg {...traco}>
      <path d="M4 6h8M4 11h5M4 16h4" />
      <circle cx="15.5" cy="14.5" r="4" />
      <path d="m18.5 17.5 2.5 2.5" />
    </svg>
  );
}

function IconeRelatorios(): JSX.Element {
  return (
    <svg {...traco}>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </svg>
  );
}

function IconeEmpresas(): JSX.Element {
  return (
    <svg {...traco}>
      <rect x="4" y="3" width="16" height="18" rx="1.5" />
      <path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" />
    </svg>
  );
}

function IconePassivo(): JSX.Element {
  return (
    <svg {...traco}>
      <path d="M12 3v18" />
      <path d="M17 6.5A3.5 3.5 0 0 0 13.5 4h-2a3.5 3.5 0 0 0 0 7h1a3.5 3.5 0 0 1 0 7h-2A3.5 3.5 0 0 1 7 17.5" />
    </svg>
  );
}

function IconeContratos(): JSX.Element {
  return (
    <svg {...traco}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </svg>
  );
}

function IconeSimulacoes(): JSX.Element {
  return (
    <svg {...traco}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 7h8M8 11h2M12 11h4M8 15h2M12 15h4" />
    </svg>
  );
}

function IconeAprovacao(): JSX.Element {
  return (
    <svg {...traco}>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </svg>
  );
}

function IconeServicosMenu(): JSX.Element {
  return (
    <svg {...traco}>
      <path d="M12 3v2M12 19v2M5 12H3M21 12h-2M6.3 6.3 4.9 4.9M19.1 19.1l-1.4-1.4M17.7 6.3l1.4-1.4M4.9 19.1l1.4-1.4" />
      <circle cx="12" cy="12" r="3.5" />
    </svg>
  );
}

/** « para recolher, » para expandir. */
function IconeRecolher(props: { readonly recolhido: boolean }): JSX.Element {
  return (
    <svg {...traco} width={18} height={18}>
      {props.recolhido ? (
        <path d="m6 17 5-5-5-5M13 17l5-5-5-5" />
      ) : (
        <path d="m18 17-5-5 5-5M11 17l-5-5 5-5" />
      )}
    </svg>
  );
}

function IconeMenu(): JSX.Element {
  return (
    <svg {...traco}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}
